/**
 * Overlay primitives — global UX standard for ASAP.
 *
 * These components MUST be used for every modal, action sheet, confirm dialog,
 * or popover anywhere in the app. They guarantee:
 *   • z-index above BottomNav (which uses 9999)
 *   • iOS/Android safe-area inset padding at the bottom (home indicator)
 *   • Scrollable content area (max-h: 90dvh) so long lists never trap CTAs
 *   • Sticky footer for primary actions — always visible above the keyboard
 *   • Works on iPhone SE (320px) through tablet, portrait + landscape
 *   • Closes on backdrop click, ESC key, and supports back-button via prop
 *   • Locks body scroll while open
 *   • Renders via React Portal at document.body — immune to ancestor transforms
 *
 * If you ever find yourself writing `fixed inset-0 z-50` for a dialog, STOP
 * and use <ConfirmDialog> or <BottomSheet> instead.
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertCircle } from 'lucide-react';

/* ── helpers ─────────────────────────────────────────────────────────── */
function useLockBodyScroll(open) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);
}

function useEscapeKey(open, onClose) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
}

/* ── <Overlay> — backdrop only (rarely used directly) ────────────────── */
export function Overlay({ open, onClose, children, testId, align = 'end', className = '' }) {
  useLockBodyScroll(open);
  useEscapeKey(open, onClose);
  if (!open || typeof document === 'undefined') return null;
  const alignClass = align === 'center' ? 'items-center' : 'items-end';
  return createPortal(
    <div
      data-testid={testId}
      onClick={onClose}
      className={`fixed inset-0 z-[10100] flex ${alignClass} justify-center bg-black/50 animate-fade-in ${className}`}
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[480px] flex flex-col items-stretch">
        {children}
      </div>
    </div>,
    document.body
  );
}

/* ── <BottomSheet> — slide-up panel anchored to the bottom ───────────── */
/**
 * Use for action sheets, pickers, and complex multi-input forms.
 * Content area is scrollable (max 90dvh). If you need a footer with CTAs,
 * pass it via the `footer` prop — it will be sticky at the bottom and
 * always visible above the home indicator.
 */
export function BottomSheet({
  open, onClose, title, children, footer, testId,
  showHandle = true, dismissible = true, contentClassName = '',
}) {
  const sheetRef = useRef(null);
  return (
    <Overlay open={open} onClose={dismissible ? onClose : undefined} testId={testId} align="end">
      <div
        ref={sheetRef}
        data-testid={testId ? `${testId}-panel` : undefined}
        className="w-full bg-white rounded-t-3xl shadow-[0_-8px_32px_rgba(0,0,0,0.18)] animate-slide-up flex flex-col"
        style={{ maxHeight: 'min(90dvh, 720px)' }}
      >
        {showHandle && (
          <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
            <div className="w-10 h-1 bg-[#E5E4DE] rounded-full" />
          </div>
        )}
        {title && (
          <div className="flex items-center justify-between px-5 pt-1 pb-3 flex-shrink-0">
            <h3 className="text-base font-extrabold text-[#111]">{title}</h3>
            {dismissible && (
              <button
                onClick={onClose}
                aria-label="Close"
                data-testid={testId ? `${testId}-close` : undefined}
                className="p-1.5 -mr-1.5 text-[#6B6E73] hover:text-[#111] active:scale-95"
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}
        <div
          className={`flex-1 overflow-y-auto overscroll-contain px-5 ${footer ? 'pb-3' : 'pb-safe-area'} ${contentClassName}`}
          style={footer ? undefined : { paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}
          data-testid={testId ? `${testId}-content` : undefined}
        >
          {children}
        </div>
        {footer && (
          <div
            className="flex-shrink-0 px-5 pt-3 border-t border-black/[0.05] bg-white"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}
            data-testid={testId ? `${testId}-footer` : undefined}
          >
            {footer}
          </div>
        )}
      </div>
    </Overlay>
  );
}

/* ── <ConfirmDialog> — alert / confirmation prompt ───────────────────── */
/**
 * Mobile-first: renders as a bottom sheet on small viewports, centered
 * modal on >=640px. Primary CTA is ALWAYS visible regardless of content
 * length, even on iPhone SE in landscape with Dynamic Type maxed out.
 */
export function ConfirmDialog({
  open, onClose, onConfirm,
  title = 'Are you sure?',
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'danger', // 'danger' | 'primary' | 'neutral'
  icon: Icon = AlertCircle,
  busy = false,
  testIdPrefix = 'confirm',
  children,
}) {
  useLockBodyScroll(open);
  useEscapeKey(open, onClose);
  if (!open || typeof document === 'undefined') return null;

  const confirmStyles = {
    danger: 'bg-[#FF3B30] text-white',
    primary: 'bg-[#111] text-white',
    neutral: 'bg-[#B6E600] text-[#111]',
  }[confirmVariant] || 'bg-[#FF3B30] text-white';

  const iconColors = {
    danger: 'text-[#FF3B30]',
    primary: 'text-[#111]',
    neutral: 'text-[#3F5200]',
  }[confirmVariant] || 'text-[#FF3B30]';

  return createPortal(
    <div
      data-testid={testIdPrefix}
      onClick={busy ? undefined : onClose}
      className="fixed inset-0 z-[10100] flex items-end sm:items-center justify-center bg-black/50 animate-fade-in"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid={`${testIdPrefix}-panel`}
        className="w-full max-w-[440px] bg-white shadow-[0_-8px_32px_rgba(0,0,0,0.18)] sm:shadow-2xl rounded-t-3xl sm:rounded-3xl sm:mx-4 sm:my-6 animate-slide-up sm:animate-fade-in flex flex-col"
        style={{ maxHeight: 'min(90dvh, 640px)' }}
      >
        {/* Drag handle on mobile only */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden flex-shrink-0">
          <div className="w-10 h-1 bg-[#E5E4DE] rounded-full" />
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-5 pt-4 pb-3">
          <div className="flex items-start gap-2.5 mb-2">
            {Icon && <Icon size={20} className={`${iconColors} flex-shrink-0 mt-0.5`} />}
            <h3 className="text-base font-extrabold text-[#111] leading-snug" data-testid={`${testIdPrefix}-title`}>
              {title}
            </h3>
          </div>
          {description && (
            <p className="text-sm text-[#6B6E73] leading-relaxed" data-testid={`${testIdPrefix}-description`}>
              {description}
            </p>
          )}
          {children && <div className="mt-3">{children}</div>}
        </div>

        {/* Sticky footer — always visible above home indicator */}
        <div
          className="flex-shrink-0 grid grid-cols-2 gap-2 px-5 pt-3 border-t border-black/[0.05] bg-white"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}
          data-testid={`${testIdPrefix}-footer`}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid={`${testIdPrefix}-cancel`}
            className="py-3 rounded-full bg-[#F1F0EA] font-bold text-sm text-[#111] active:scale-[0.98] disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid={`${testIdPrefix}-proceed`}
            className={`py-3 rounded-full font-bold text-sm active:scale-[0.98] disabled:opacity-50 ${confirmStyles}`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
